/**
 * SingleVoiceController - Clean single-voice Howler.js wrapper
 *
 * Encapsulates ALL Howler sound-ID tracking and playback state in one place.
 * Replaces scattered activeSoundIdRef, timing-based heuristics, and
 * multi-layered guard logic with a simple state machine.
 *
 * Design rationale:
 * - Howler.js `play()` without an ID creates a NEW concurrent sound.
 * - Howler.js `play(id)` resumes an existing paused sound.
 * - We only ever want ONE sound playing (single-voice). The controller
 *   tracks the active sound ID and always passes it to Howler operations.
 *
 * State machine:
 *   Idle ──► Playing ──► Paused ──► Playing (resume)
 *     ▲         │           │
 *     │         ▼           │
 *     └──── (stop/end) ◄───┘
 *
 * @see https://github.com/goldfire/howler.js/issues/1702
 */

import { Howl } from 'howler';
import { Logger } from '@/app/utils/logger';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export enum VoiceState {
  Idle = 'idle',
  Playing = 'playing',
  Paused = 'paused',
}

export interface VoiceStateChangeEvent {
  state: VoiceState;
  soundId: number | null;
}

export interface SingleVoiceCallbacks {
  /** Fired whenever the controller's state changes. */
  onStateChange: (event: VoiceStateChangeEvent) => void;
}

// ---------------------------------------------------------------------------
// Controller
// ---------------------------------------------------------------------------

const LOG_TAG = 'SingleVoiceController';

export class SingleVoiceController {
  // ---- Internal state ----
  private howl: Howl | null = null;
  private activeSoundId: number | null = null;
  private voiceState: VoiceState = VoiceState.Idle;
  private isHtml5: boolean;
  private callbacks: SingleVoiceCallbacks;

  /**
   * True while we have issued a play/pause/stop/seek call and have not yet
   * received the corresponding Howl callback confirming it. External-event
   * handlers use this flag to distinguish app-initiated operations from
   * browser/OS-initiated ones (replacing the old timing-based heuristic).
   */
  private internalActionPending = false;

  constructor(callbacks: SingleVoiceCallbacks, isHtml5: boolean) {
    this.callbacks = callbacks;
    this.isHtml5 = isHtml5;
  }

  // -------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------

  /** Attach a newly-created Howl instance. */
  attach(howl: Howl): void {
    this.howl = howl;
    this.activeSoundId = null;
    this.voiceState = VoiceState.Idle;
    this.internalActionPending = false;
  }

  /** Detach and reset – call on cleanup / track switch. */
  detach(): void {
    this.howl = null;
    this.activeSoundId = null;
    this.voiceState = VoiceState.Idle;
    this.internalActionPending = false;
  }

  // -------------------------------------------------------------------
  // Playback controls (called by hook / consumer)
  // -------------------------------------------------------------------

  /**
   * Play or resume the single voice.
   *
   * - If already Playing → no-op (prevents duplicates).
   * - If we have an activeSoundId → resume via `play(id)`.
   * - Otherwise → first play via `play()` and capture the returned ID.
   *
   * @returns the sound ID (useful for logging / debug).
   */
  play(): number {
    if (!this.howl) throw new Error('No Howl attached');

    // Already playing – skip. This single check replaces the old 3-branch guard.
    if (this.voiceState === VoiceState.Playing) {
      Logger.debug('play() skipped — already playing', LOG_TAG, {
        soundId: this.activeSoundId,
      });
      return this.activeSoundId ?? 0;
    }

    this.internalActionPending = true;

    if (this.activeSoundId !== null) {
      // Resume the existing sound – NEVER create a new one.
      Logger.debug('Resuming tracked sound', LOG_TAG, {
        soundId: this.activeSoundId,
      });
      this.howl.play(this.activeSoundId);
      return this.activeSoundId;
    }

    // First play after load – create a new sound and capture the ID.
    const soundId = this.howl.play();
    this.activeSoundId = soundId;
    Logger.debug('First play — captured sound ID', LOG_TAG, { soundId });
    return soundId;
  }

  /** Pause the active sound. */
  pause(): void {
    if (!this.howl) return;

    this.internalActionPending = true;

    if (this.activeSoundId !== null) {
      this.howl.pause(this.activeSoundId);
    } else {
      this.howl.pause();
    }

    Logger.debug('Pause requested', LOG_TAG, { soundId: this.activeSoundId });
  }

  /** Stop the active sound and reset to idle. */
  stop(): void {
    if (!this.howl) return;

    this.internalActionPending = true;

    if (this.activeSoundId !== null) {
      this.howl.stop(this.activeSoundId);
    } else {
      this.howl.stop();
    }

    this.activeSoundId = null;
    this.setState(VoiceState.Idle);

    Logger.debug('Stop requested', LOG_TAG);
  }

  /** Seek to a specific position (seconds). */
  seek(time: number): void {
    if (!this.howl) return;

    // Seeking in Howler triggers an internal pause+play cycle, so mark
    // as internal to suppress external-event detection.
    this.internalActionPending = true;

    if (this.activeSoundId !== null) {
      this.howl.seek(time, this.activeSoundId);
    } else {
      this.howl.seek(time);
    }
  }

  // -------------------------------------------------------------------
  // Howl event handlers (wired into Howl's onplay/onpause/onend)
  // -------------------------------------------------------------------

  /**
   * Called by Howl's `onplay` callback to confirm playback started.
   * This is the ONLY place that sets state to Playing (except syncExternalPlay).
   */
  handleHowlPlay(soundId: number): void {
    this.internalActionPending = false;
    this.activeSoundId = soundId;
    this.setState(VoiceState.Playing);
  }

  /**
   * Called by Howl's `onpause` callback.
   * Ignores events for sounds that are not ours (e.g. phantom sounds).
   */
  handleHowlPause(soundId: number): void {
    if (!this.isOurSound(soundId)) return;

    this.internalActionPending = false;
    this.setState(VoiceState.Paused);
  }

  /**
   * Called by Howl's `onend` callback.
   * Transitions to Idle since the sound completed naturally.
   */
  handleHowlEnd(soundId: number): void {
    if (!this.isOurSound(soundId)) return;

    this.internalActionPending = false;
    this.setState(VoiceState.Idle);
  }

  // -------------------------------------------------------------------
  // External event coordination
  // -------------------------------------------------------------------

  /**
   * Whether an internal operation (play/pause/stop/seek) is currently in
   * flight and hasn't been confirmed by the Howl callback yet.
   *
   * External event handlers should ignore native audio events when this
   * returns true, because those events were triggered by our own code.
   */
  isInternalActionPending(): boolean {
    return this.internalActionPending;
  }

  /**
   * Called when the native HTMLAudioElement fires a 'play' event that was
   * NOT initiated by our code (e.g. Safari tab resume, OS media controls).
   */
  syncExternalPlay(): void {
    // Recover the sound ID from Howler internals if we don't have one.
    if (this.activeSoundId === null && this.howl) {
      this.activeSoundId = this.recoverSoundId();
    }

    this.setState(VoiceState.Playing);
  }

  /**
   * Called when the native HTMLAudioElement fires a 'pause' event that was
   * NOT initiated by our code (e.g. headphone disconnect, OS controls).
   */
  syncExternalPause(): void {
    this.setState(VoiceState.Paused);
  }

  /**
   * Called after an internal action's corresponding native event has fired
   * (via the HTMLAudioElement listener). This clears the pending flag so
   * that subsequent native events are correctly treated as external.
   *
   * This is needed because sometimes the Howl callback fires AFTER the
   * native event (e.g. Howl's onplay uses setTimeout(0)), so the flag
   * might still be set when a legitimate external event arrives later.
   */
  clearInternalActionPending(): void {
    this.internalActionPending = false;
  }

  // -------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------

  getState(): VoiceState { return this.voiceState; }
  getActiveSoundId(): number | null { return this.activeSoundId; }
  isCurrentlyPlaying(): boolean { return this.voiceState === VoiceState.Playing; }

  /** Check if a sound ID belongs to our tracked active sound. */
  isOurSound(soundId: number): boolean {
    return this.activeSoundId === null || soundId === this.activeSoundId;
  }

  /**
   * Get the underlying HTMLAudioElement (html5 mode only).
   *
   * This is the ONLY place that accesses Howler's internal `_sounds` array
   * in the normal flow. All other code uses this accessor.
   */
  getAudioElement(): HTMLAudioElement | null {
    if (!this.howl || !this.isHtml5) return null;

    try {
      const sounds = (this.howl as unknown as {
        _sounds: Array<{ _node: HTMLAudioElement }>;
      })._sounds;
      if (!sounds || sounds.length === 0) return null;

      const audioNode = sounds[0]._node;
      return audioNode instanceof HTMLAudioElement ? audioNode : null;
    } catch {
      return null;
    }
  }

  /**
   * Audit Howler's internal sound pool for orphaned "phantom" sounds.
   *
   * Safari's AudioContext suspension can leave extra sounds in Howler's
   * `_sounds[]` array. This method stops any sound that isn't our tracked
   * activeSoundId. Should be called on tab resume (visibilitychange).
   *
   * This is the ONLY other place that accesses `_sounds`, and it's a
   * debug/safety-net mechanism, not part of the normal playback flow.
   */
  auditPhantomSounds(): void {
    if (!this.howl) return;

    try {
      const howl = this.howl as unknown as {
        _sounds: Array<{ _id: number; _paused: boolean; _ended: boolean }>;
      };
      const sounds = howl._sounds;

      if (!sounds || sounds.length <= 1) return;

      Logger.warn('Phantom sounds detected, cleaning up', LOG_TAG, {
        soundCount: sounds.length,
        activeSoundId: this.activeSoundId,
      });

      for (const sound of sounds) {
        if (sound._id !== this.activeSoundId) {
          Logger.info('Stopping phantom sound', LOG_TAG, { phantomId: sound._id });
          this.howl!.stop(sound._id);
        }
      }
    } catch {
      // Safe to ignore — internal structure access may fail across Howler versions
    }
  }

  // -------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------

  private setState(newState: VoiceState): void {
    if (newState === this.voiceState) return;

    const oldState = this.voiceState;
    this.voiceState = newState;

    Logger.debug(`State: ${oldState} → ${newState}`, LOG_TAG, {
      soundId: this.activeSoundId,
    });

    this.callbacks.onStateChange({
      state: newState,
      soundId: this.activeSoundId,
    });
  }

  /**
   * Recover the active sound ID from Howler's internal `_sounds` array.
   * Used as a fallback when an external event plays audio before we've
   * captured the sound ID through our normal flow.
   */
  private recoverSoundId(): number | null {
    if (!this.howl) return null;

    try {
      const howl = this.howl as unknown as {
        _sounds: Array<{ _id: number }>;
      };
      if (howl._sounds?.length > 0) {
        const id = howl._sounds[0]._id;
        Logger.debug('Recovered sound ID from Howler internals', LOG_TAG, { soundId: id });
        return id;
      }
    } catch { /* safe to ignore */ }

    return null;
  }
}
